// src/components/Dashboard/Dashboard.tsx
import {
  HbrIcon,
  HbrSpinner,
  HbrTab,
  HbrTabGroup,
} from '@harbor/elements-react-standalone';
import {
  alertsNegativeSmallBorder,
} from '@harbor/elements/icons';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useFetchCustomTiles } from '../../hooks/toolbox-hooks';
import { CustomTile, RegistryTile, TileDefinition } from '../../types/dashboard';
import DashboardConfigureModal from './Configure/DashboardConfigureModal';
import RssFeedComponent from './RssFeedComponent';
import StbButton from '../security-toolbox-ui/StbButton';
import StbModal from '../security-toolbox-ui/StbModal';
import { useFetchUser } from '../../hooks/user-hooks';
import DashboardGridLayout from './DashboardGrid/DashboardGridLayout';
import { useUserRegistry } from '../../hooks/useUserRegistry';
import { RootState } from '../../support/store';
import { useSelector } from 'react-redux';


// Grid constants
const MIN_SIZE = 1;
const MAX_SIZE = 8;
const TILE_SHADOW_PADDING = 4;

const Dashboard: React.FC = () => {
  const {
    registry,
    registryReady,
    registryError,
    userDashboards,
    updateUserDashboard,
  } = useUserRegistry();
  const { minimizeMenu: menuMinimized } = useSelector((state: RootState) => state.ui);
  const { data: user, error: userError, isLoading: userLoading } = useFetchUser();

  // Custom tiles
  const { data: customTileData, error: customTileDataError } = useFetchCustomTiles();
  const customTiles: Record<CustomTile['id'], CustomTile> = useMemo(() => (customTileData ?? []).reduce((acc, tile) => {
    acc[Number(tile.id)] = tile;
    return acc;
  }, {}), [customTileData]);

  // Custom Tile errors
  const [tileErrors, setTileErrors] = useState<{[tileId: CustomTile['id']]: string | null}>({});

  // Set the current selected dashboard
  const [selectedDashboard, setSelectedDashboard] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedDashboard && Object.keys(userDashboards).length > 0) {
      setSelectedDashboard(Object.keys(userDashboards)[0]);
    }
  }, [userDashboards, selectedDashboard]);

  // Ensure at least one dashboard always exists. Without a dashboard the Configure
  // modal lets you toggle tiles that have nowhere to be saved (handleUpdateTiles
  // early-returns when nothing is selected), so seed a default "Dashboard".
  const defaultDashboardCreatedRef = useRef(false);
  useEffect(() => {
    if (
      registryReady &&
      registry &&
      !defaultDashboardCreatedRef.current &&
      Object.keys(userDashboards).length === 0
    ) {
      defaultDashboardCreatedRef.current = true;
      const id = uuidv4();
      updateUserDashboard({
        [id]: {
          id,
          name: 'Dashboard',
          tiles: [],
          isRssVisible: false,
        },
      });
    }
  }, [registryReady, registry, userDashboards, updateUserDashboard]);

  // RSS feed visibility
  const [isRssVisible, setIsRssVisible] = useState<boolean>(false);
  useEffect(() => {
    if (selectedDashboard && userDashboards[selectedDashboard]) {
      setIsRssVisible(!!userDashboards[selectedDashboard]?.isRssVisible);
    }
  }, [selectedDashboard, userDashboards]);

  // Config dashboard modal
  const [configModalOpen, setConfigModalOpen] = useState(false);

  // Dashboard tiles
  const dashboardTiles: {[dashboardId: string]: TileDefinition[]} = useMemo(() => {
    return Object.keys(userDashboards).reduce((dash, dashboardId) => {
      dash[dashboardId] = (userDashboards[dashboardId]?.tiles ?? []).map((tile) => {
        const customTile = customTiles[tile.id];
        const tileError = tileErrors[tile.id] || null;
        return {
          ...(customTile ?? {}),
          ...tile,
          error: tileError ?? (customTile ? null : 'Tile Error: Custom tile definition not found'),
        };
      });
      
      return dash;
    }, {});
  }, [userDashboards, customTiles, tileErrors]);

  // Set window resize behavior
  const layoutRef = useRef<HTMLDivElement>(null);
  const [layoutWidth, setLayoutWidth] = useState(640 - TILE_SHADOW_PADDING);
  const layoutRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node !== null) {
      layoutRef.current = node;
      const width = node.clientWidth;
      setLayoutWidth(Math.max(width, 640) - TILE_SHADOW_PADDING);
    }
  }, []);

  useEffect(() => {
    const updateLayoutSize = () => {
      try {
        if (layoutRef.current) {
          const newWidth =
            Math.max(layoutRef.current.clientWidth, 640) - TILE_SHADOW_PADDING;
          setLayoutWidth(newWidth);
        }
      } catch (e) {
        console.error('Error updating layout size:', e);
      }
    };

    let timeout: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        updateLayoutSize();
      }, 100);
    };

    window.addEventListener('resize', onResize);

    // run immediately
    updateLayoutSize();

    // run again after layout settles (important for nav collapse/expand)
    const settleTimer = setTimeout(updateLayoutSize, 150);

    return () => {
      clearTimeout(timeout);
      clearTimeout(settleTimer);
      window.removeEventListener('resize', onResize);
    };
  }, [isRssVisible, selectedDashboard, menuMinimized]);

  const handleUpdateDashboard = async (dashboardId: string, tiles: RegistryTile[]) => updateUserDashboard({
    ...userDashboards,
    [dashboardId]: {
      ...userDashboards[dashboardId],
      tiles,
    },
  });

  /**
   * Calculate scaling based on defined boundary size in tile settings and on available layout size.
   * Needed because charts will dynamically change and burst out of bounds, covering other tiles and controls.
   *
   * We shrink to whichever axis is the most squished.
   * Charts are kept top left on the card.
   */

  if (!user || !customTileData) {
    return (
      <div>
        <HbrSpinner size='large'>
          <div slot='label'>Loading...</div>
        </HbrSpinner>
      </div>
    );
  }

  if (userError || registryError || customTileDataError) {
    return (
      <div
        className="p-3 rounded-md m-4 flex items-start gap-3 text-sm"
        style={{ border: "1px solid var(--hbr-color-danger-35)" }}
      >
        <HbrIcon asset={alertsNegativeSmallBorder} size="20px" sentiment="danger" />
        <div className="flex flex-col gap-1">
          <div>Error loading dashboard. Please try again later.</div>
          {registryError && (
            <div>User error: User registry not found</div>
          )}
          {userError && (
            <div>User error: {String((userError as any)?.message || "User not found")}</div>
          )}
          {customTileDataError && (
            <div>Custom tile error: {String((customTileDataError as any)?.message || "Custom tiles not found")}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-full flex flex-col gap-2 overflow-hidden">
        {/* Dashboard Navigation */}
        <div className="flex flex-row items-center px-6">
          <div className="flex justify-start flex-1 min-w-0">
            <HbrTabGroup className="view-navigation" placement="top">
              {Object.keys(userDashboards).map((dashboardId) => (
                <HbrTab
                  key={dashboardId}
                  active={selectedDashboard === dashboardId}
                  onHbr-click={() => setSelectedDashboard(dashboardId)}
                  slot='nav'
                  className="view-navigation"
                >
                  {userDashboards[dashboardId].name}
                </HbrTab>
              ))}
            </HbrTabGroup>
          </div>

          <div className="flex ml-auto">
            <StbButton
              role='update'
              onClick={() => setConfigModalOpen(true)}
            >
              Configure Dashboards
            </StbButton>
          </div>
        </div>

        {/* Dashboard */}
        <div className={`mx-6 mt-2 grid gap-2 overflow-hidden flex-1 ${
          isRssVisible
            ? '2xl:grid-cols-[minmax(640px,2fr)_minmax(200px,1fr)]'
            : '2xl:grid-cols-1'
          }`}
        >
          <div ref={layoutRefCallback} style={{ height: '100%', overflow: 'auto', minHeight: 0 }}>
            {selectedDashboard && (
              <DashboardGridLayout
                selectedDashboard={selectedDashboard}
                tiles={dashboardTiles[selectedDashboard] ?? []}
                width={layoutWidth}
                handleUpdateDashboard={handleUpdateDashboard}
                setTileErrors={setTileErrors}
              />
            )}
          </div>

          <div
            style={{
              flexBasis: isRssVisible
                ? '25%'
                : '0',
              transition: 'flex-basis 0.3s ease',
              overflowY: 'visible',
              overflowX: 'hidden',
              backgroundColor: '#f8f8f8',
              display: isRssVisible
                ? 'block'
                : 'none',
            }}
          >
            {isRssVisible && <RssFeedComponent />}
          </div>
        </div>
      </div>

      <StbModal
        open={configModalOpen}
        role="default"
        onClose={async () => setConfigModalOpen(false)}
        submitLabel="Close"
        hideCancel={true}
        size="large"
      >
        <DashboardConfigureModal
          dashboards={userDashboards}
          selected={selectedDashboard}
          setSelected={setSelectedDashboard}
        />
      </StbModal>
    </>
  );
};

export default Dashboard;
